
import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { cleanupAuthState, authRateLimiter } from '@/utils/authSecurity';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  isCompany: boolean;
  signOut: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<{ error?: any }>;
  signUp: (email: string, password: string, metadata?: any) => Promise<{ error?: any }>;
  login: (email: string, password: string) => Promise<{ user: User; session: Session } | null>;
  logout: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const isAdmin = Boolean(user?.email?.endsWith('@redflags.au'));
  const isCompany = Boolean(user?.user_metadata?.user_type === 'company');

  useEffect(() => {
    // Set up auth state listener first
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log(`Auth event: ${event}`, { hasSession: !!session });
        setSession(session);
        setUser(session?.user ?? null);
        
        // Only set loading to false after we've processed the auth state
        if (event === 'INITIAL_SESSION' || event === 'SIGNED_OUT' || event === 'SIGNED_IN') {
          setLoading(false);
        }
      }
    );

    // Then check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    try {
      cleanupAuthState();
      await supabase.auth.signOut({ scope: 'global' });
      setUser(null);
      setSession(null);
      window.location.href = '/';
    } catch (error) {
      console.error('Sign out error:', error);
      // Force cleanup even if sign out fails
      setUser(null);
      setSession(null);
      window.location.href = '/';
    }
  };

  const logout = signOut; // Alias for backward compatibility

  const signIn = async (email: string, password: string) => {
    // Check rate limiting
    if (authRateLimiter.isRateLimited(email)) {
      const remainingTime = Math.ceil(authRateLimiter.getRemainingTime(email) / 1000 / 60);
      return { 
        error: { 
          message: `Too many failed attempts. Please try again in ${remainingTime} minutes.` 
        } 
      };
    }

    try {
      // Clean up existing state
      cleanupAuthState();
      
      // Attempt global sign out first
      try {
        await supabase.auth.signOut({ scope: 'global' });
      } catch (err) {
        // Continue even if this fails
      }
      
      // Sign in with email/password
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.toLowerCase().trim(),
        password,
      });
      
      if (error) {
        authRateLimiter.recordAttempt(email);
        throw error;
      }
      
      if (data.user) {
        // Force page reload for clean state
        window.location.href = '/';
        return { error: null };
      }
      
      return { error: null };
    } catch (error: any) {
      console.error('Sign in error:', error);
      return { error };
    }
  };

  const login = async (email: string, password: string): Promise<{ user: User; session: Session } | null> => {
    try {
      const result = await signIn(email, password);
      if (result.error) {
        throw new Error(result.error.message);
      }
      
      // Return current user and session if available
      if (user && session) {
        return { user, session };
      }
      
      return null;
    } catch (error: any) {
      throw error;
    }
  };

  const signUp = async (email: string, password: string, metadata?: any) => {
    // Check rate limiting
    if (authRateLimiter.isRateLimited(email)) {
      const remainingTime = Math.ceil(authRateLimiter.getRemainingTime(email) / 1000 / 60);
      return { 
        error: { 
          message: `Too many attempts. Please try again in ${remainingTime} minutes.` 
        } 
      };
    }

    try {
      // Clean up existing state
      cleanupAuthState();
      
      const { data, error } = await supabase.auth.signUp({
        email: email.toLowerCase().trim(),
        password,
        options: {
          data: metadata || {},
          emailRedirectTo: window.location.origin,
        },
      });

      if (error) {
        authRateLimiter.recordAttempt(email);
        throw error;
      }

      return { error: null };
    } catch (error: any) {
      console.error('Sign up error:', error);
      return { error };
    }
  };

  const signInWithGoogle = async () => {
    try {
      cleanupAuthState();
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin,
        }
      });
      
      if (error) {
        throw error;
      }
    } catch (error: any) {
      console.error('Google sign in error:', error);
      throw error;
    }
  };

  const value = {
    user,
    session,
    loading,
    isAdmin,
    isCompany,
    signOut,
    signIn,
    signUp,
    login,
    logout,
    signInWithGoogle,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
